import { useState, useEffect, useRef } from 'preact/hooks';
import type { Message, ContactInfo, I18nStrings, ProjectSpec, Attachment, ConversationMode, LeaveMessageRequest } from '@bitio-builder/shared';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ContactForm } from './ContactForm';
import { ProjectSpecPanel } from './ProjectSpecPanel';
import { SpecConfirmation } from './SpecConfirmation';
import { ModeSelector } from './ModeSelector';
import { LeaveMessageForm } from './LeaveMessageForm';
import { IntroForm, type UserIntro } from './IntroForm';
import type { ChatStatus } from '../hooks/useChat';

interface ChatWindowProps {
  isOpen: boolean;
  status: ChatStatus;
  mode: ConversationMode | null;
  title: string;
  messages: Message[];
  showSpecReview: boolean;
  showContactForm: boolean;
  showLeaveMessage: boolean;
  showNoReplyFallback: boolean;
  isWithinOfficeHours: boolean;
  error: string | null;
  strings: I18nStrings;
  projectSpec: ProjectSpec | null;
  conversationSummary: string | null;
  isWaitingForResponse: boolean;
  uploadedAttachments: Attachment[];
  onSendMessage: (message: string) => void;
  onFileUpload: (file: File) => Promise<Attachment | null>;
  onRemoveAttachment: (attachmentId: string) => void;
  onSubmitContact: (contactInfo: ContactInfo) => void;
  onConfirmSpec: (editedSpec: ProjectSpec) => void;
  onContinueRefining: () => void;
  onSubmitSpecEarly: () => void;
  onSelectMode: (mode: ConversationMode, userIntro?: UserIntro) => void;
  onLeaveMessage: () => void;
  onHideLeaveMessage: () => void;
  onSubmitLeaveMessage: (data: LeaveMessageRequest) => Promise<boolean>;
  onReset: () => void;
  onClose: () => void;
}

type ActiveTab = 'chat' | 'spec';

const windowStyle: Record<string, string | number> = {
  position: 'absolute',
  bottom: '80px',
  right: '0',
  width: '380px',
  maxWidth: 'calc(100vw - 40px)',
  height: '580px',
  maxHeight: 'calc(100vh - 120px)',
  background: 'var(--bb-background, #FAFAFA)',
  borderRadius: '16px',
  boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const windowExpandedStyle: Record<string, string | number> = {
  ...windowStyle,
  width: '660px',
};

const headerStyle: Record<string, string | number> = {
  padding: '12px 16px',
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
};

const titleContainerStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const titleTextStyle: Record<string, string | number> = {
  fontSize: '14px',
  fontWeight: 600,
  margin: 0,
};

const headerButtonsStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const iconButtonStyle: Record<string, string | number> = {
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  opacity: 0.8,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  borderRadius: '4px',
};

const iconButtonActiveStyle: Record<string, string | number> = {
  ...iconButtonStyle,
  background: 'rgba(255,255,255,0.15)',
  opacity: 1,
};

const tabContainerStyle: Record<string, string | number> = {
  display: 'flex',
  borderBottom: '1px solid var(--bb-border, #E5E7EB)',
};

const tabButtonStyle = (active: boolean): Record<string, string | number> => ({
  flex: 1,
  padding: '10px 16px',
  background: 'transparent',
  border: 'none',
  borderBottom: active ? '2px solid var(--bb-primary, #4D5D32)' : '2px solid transparent',
  color: active ? 'var(--bb-primary, #4D5D32)' : 'var(--bb-muted, #6B7280)',
  fontWeight: active ? 600 : 400,
  fontSize: '13px',
  cursor: 'pointer',
  transition: 'all 150ms ease',
});

const contentContainerStyle: Record<string, string | number> = {
  flex: 1,
  display: 'flex',
  overflow: 'hidden',
};

const chatPanelStyle: Record<string, string | number> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minWidth: 0,
};

const specPanelDesktopStyle: Record<string, string | number> = {
  width: '280px',
  borderLeft: '1px solid var(--bb-border, #E5E7EB)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const loadingStyle: Record<string, string | number> = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const spinnerStyle: Record<string, string | number> = {
  width: '40px',
  height: '40px',
  border: '3px solid var(--bb-border, #E5E7EB)',
  borderTopColor: 'var(--bb-primary, #4D5D32)',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
};

const completedStyle: Record<string, string | number> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px',
  textAlign: 'center',
};

const successIconStyle: Record<string, string | number> = {
  width: '64px',
  height: '64px',
  background: 'var(--bb-success, #059669)',
  color: 'white',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
};

const successTitleStyle: Record<string, string | number> = {
  fontSize: '16px',
  fontWeight: 600,
  marginBottom: '8px',
};

const successSubtitleStyle: Record<string, string | number> = {
  color: 'var(--bb-muted, #6B7280)',
};

const errorStyle: Record<string, string | number> = {
  padding: '8px 16px',
  background: '#fef2f2',
  borderBottom: '1px solid #fecaca',
  color: 'var(--bb-error, #DC2626)',
  fontSize: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const modeIndicatorStyle: Record<string, string | number> = {
  padding: '6px 12px',
  background: 'var(--bb-muted-bg, #F3F4F6)',
  borderBottom: '1px solid var(--bb-border, #E5E7EB)',
  fontSize: '12px',
  color: 'var(--bb-muted, #6B7280)',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CheckIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style={{ width: '32px', height: '32px', flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SpecIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style={{ width: '18px', height: '18px', flexShrink: 0 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style={{ width: '18px', height: '18px', flexShrink: 0 }}>
    <path d="M19 12H5" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

export function ChatWindow({
  isOpen,
  status,
  mode,
  title,
  messages,
  showSpecReview,
  showContactForm,
  showLeaveMessage,
  showNoReplyFallback,
  isWithinOfficeHours,
  error,
  strings,
  projectSpec,
  conversationSummary,
  isWaitingForResponse,
  uploadedAttachments,
  onSendMessage,
  onFileUpload,
  onRemoveAttachment,
  onSubmitContact,
  onConfirmSpec,
  onContinueRefining,
  onSubmitSpecEarly,
  onSelectMode,
  onLeaveMessage,
  onHideLeaveMessage,
  onSubmitLeaveMessage,
  onReset,
  onClose,
}: ChatWindowProps) {
  const [specPanelOpen, setSpecPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('chat');
  const [isMobile, setIsMobile] = useState(false);
  const hasAutoOpenedRef = useRef(false);
  const [showIntroForm, setShowIntroForm] = useState(false);
  const [pendingMode, setPendingMode] = useState<ConversationMode | null>(null);

  const hasSpecData =
    projectSpec &&
    (projectSpec.projectName ||
      projectSpec.targetAudience ||
      (projectSpec.features && projectSpec.features.length > 0));

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-open spec panel when data becomes available (desktop only, once per session)
  useEffect(() => {
    if (hasSpecData && !isMobile && !hasAutoOpenedRef.current) {
      setSpecPanelOpen(true);
      hasAutoOpenedRef.current = true;
    }
  }, [hasSpecData, isMobile]);

  if (!isOpen) return null;

  const showDesktopPanel = !isMobile && specPanelOpen && hasSpecData && mode === 'claude';
  const showMobileTabs = isMobile && hasSpecData && status === 'active' && mode === 'claude';

  return (
    <div style={showDesktopPanel ? windowExpandedStyle : windowStyle}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div style={headerStyle}>
        <div style={titleContainerStyle}>
          <h2 style={titleTextStyle}>{title}</h2>
        </div>
        <div style={headerButtonsStyle}>
          {mode && status !== 'idle' && status !== 'completed' && (
            <button
              style={iconButtonStyle}
              onClick={onReset}
              title={strings.chat.back}
            >
              <BackIcon />
            </button>
          )}
          {!isMobile && hasSpecData && mode === 'claude' && (
            <button
              style={specPanelOpen ? iconButtonActiveStyle : iconButtonStyle}
              onClick={() => setSpecPanelOpen(!specPanelOpen)}
              title={strings.projectSpec.title}
            >
              <SpecIcon />
            </button>
          )}
          <button style={iconButtonStyle} onClick={onClose} aria-label={strings.closeChat}>
            <CloseIcon />
          </button>
        </div>
      </div>

      {error && (
        <div style={errorStyle}>
          <span>{error}</span>
        </div>
      )}

      {mode === 'slack' && status === 'active' && (
        <div style={modeIndicatorStyle}>
          <span>💬</span>
          <span>{strings.slackMode.chatting}</span>
        </div>
      )}

      {mode === 'claude' && status === 'active' && (
        <div style={modeIndicatorStyle}>
          <span>🤖</span>
          <span>{strings.slackMode.specBuilder}</span>
        </div>
      )}

      {showMobileTabs && !showContactForm && (
        <div style={tabContainerStyle}>
          <button style={tabButtonStyle(activeTab === 'chat')} onClick={() => setActiveTab('chat')}>
            {strings.projectSpec.tabChat}
          </button>
          <button style={tabButtonStyle(activeTab === 'spec')} onClick={() => setActiveTab('spec')}>
            {strings.projectSpec.tabSpec}
          </button>
        </div>
      )}

      <div style={contentContainerStyle}>
        {status === 'idle' && !showLeaveMessage && !showIntroForm && (
          <ModeSelector
            strings={strings}
            onSelectMode={(mode) => {
              // For slack mode, show intro form first
              if (mode === 'slack') {
                setPendingMode(mode);
                setShowIntroForm(true);
              } else {
                onSelectMode(mode);
              }
            }}
            onLeaveMessage={onLeaveMessage}
            isLoading={false}
            isWithinOfficeHours={isWithinOfficeHours}
          />
        )}

        {status === 'idle' && !showLeaveMessage && showIntroForm && (
          <IntroForm
            strings={strings}
            onSubmit={(userIntro) => {
              if (pendingMode) {
                onSelectMode(pendingMode, userIntro);
                setShowIntroForm(false);
                setPendingMode(null);
              }
            }}
            onBack={() => {
              setShowIntroForm(false);
              setPendingMode(null);
            }}
            isLoading={false}
          />
        )}

        {status === 'idle' && showLeaveMessage && (
          <LeaveMessageForm
            strings={strings}
            onSubmit={onSubmitLeaveMessage}
            isOutsideHours={!isWithinOfficeHours}
            onBack={onHideLeaveMessage}
          />
        )}

        {status === 'loading' && (
          <IntroForm
            strings={strings}
            onSubmit={() => {}}
            onBack={() => {}}
            isLoading={true}
          />
        )}

        {status === 'completed' && (
          <div style={completedStyle}>
            <div style={successIconStyle}>
              <CheckIcon />
            </div>
            <h3 style={successTitleStyle}>{strings.contactForm.success}</h3>
            <p style={successSubtitleStyle}>Vi ser fram emot att hjälpa dig!</p>
          </div>
        )}

        {(status === 'active' || status === 'completing') && (
          <>
            {showSpecReview ? (
              <SpecConfirmation
                projectSpec={projectSpec}
                conversationSummary={conversationSummary}
                strings={strings}
                onConfirm={onConfirmSpec}
                onContinue={onContinueRefining}
              />
            ) : showContactForm ? (
              <ContactForm
                onSubmit={onSubmitContact}
                isSubmitting={status === 'completing'}
                strings={strings}
              />
            ) : (
              <>
                {/* Mobile: Show based on active tab */}
                {isMobile ? (
                  activeTab === 'chat' ? (
                    <div style={chatPanelStyle}>
                      <MessageList
                        messages={messages}
                        isLoading={isWaitingForResponse}
                        showNoReplyFallback={mode === 'slack' && showNoReplyFallback}
                        strings={strings}
                        onSubmitLeaveMessage={onSubmitLeaveMessage}
                      />
                      <MessageInput
                        onSend={onSendMessage}
                        onFileUpload={onFileUpload}
                        disabled={status === 'completing'}
                        strings={strings}
                      />
                    </div>
                  ) : (
                    <ProjectSpecPanel
                      projectSpec={projectSpec}
                      strings={strings}
                      onSendMessage={onSendMessage}
                      onSubmitSpecEarly={onSubmitSpecEarly}
                      isWaitingForResponse={isWaitingForResponse}
                      uploadedAttachments={uploadedAttachments}
                      onFileUpload={onFileUpload}
                      onRemoveAttachment={onRemoveAttachment}
                    />
                  )
                ) : (
                  /* Desktop: Show chat + optional side panel */
                  <>
                    <div style={chatPanelStyle}>
                      <MessageList
                        messages={messages}
                        isLoading={isWaitingForResponse}
                        showNoReplyFallback={mode === 'slack' && showNoReplyFallback}
                        strings={strings}
                        onSubmitLeaveMessage={onSubmitLeaveMessage}
                      />
                      <MessageInput
                        onSend={onSendMessage}
                        onFileUpload={onFileUpload}
                        disabled={status === 'completing'}
                        strings={strings}
                      />
                    </div>
                    {showDesktopPanel && (
                      <div style={specPanelDesktopStyle}>
                        <ProjectSpecPanel
                          projectSpec={projectSpec}
                          strings={strings}
                          onSendMessage={onSendMessage}
                          onSubmitSpecEarly={onSubmitSpecEarly}
                          isWaitingForResponse={isWaitingForResponse}
                          uploadedAttachments={uploadedAttachments}
                          onFileUpload={onFileUpload}
                          onRemoveAttachment={onRemoveAttachment}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
